import React, { useContext, useEffect, useState, type FC, type Ref } from "react";

import { Upload } from "antd";
import ImgCrop from "antd-img-crop";
import { Formik } from "formik";
import MaskedInput from "react-text-mask";
import { toast } from "sonner";
import * as Yup from "yup";

import { Button, Card, Field, Input, Switch } from "@/components/ui";
import { Upload as UploadIcon, Trash2 } from "lucide-react";
import { UserChangePasswordForm } from "@/containers/UserChangePasswordForm";
import { ConfigContext } from "@/contexts/appContexts";
import { AppContext } from "@/providers";
import { SocialCoachSessionService } from "@/services";
import "../../../styling/antd.scss";
import { getImageAsBase64, phoneNumberMask, phoneRegExp, Rules, saveImageToFirebase } from "@/utils";

const validationProfileSchema = Yup.object().shape({
  firstName: Yup.string().trim().required("First Name is required"),
  lastName: Yup.string().trim().required("Last Name is required"),
  phoneNumber: Yup.string().matches(phoneRegExp, "Phone number is not valid").required("Phone number is required"),
  organizationTitle: Yup.string()
    .trim()
    .required("Title is required")
    .max(Rules.MAX_PROFILE_TITLE, "The maximum length for Title is 50 characters."),
  contactPhone: Yup.string().matches(phoneRegExp, "Contact Phone number is not valid."),
  contactEmail: Yup.string().email("Contact email is not valid."),
});

const CoachProfile: FC = () => {
  const { userContext, refreshSession } = useContext(AppContext);
  const config = useContext(ConfigContext);

  const [img, setImg] = useState<File>();
  const [imageFile, setImageFile] = useState(userContext?.profile?.photoUrl);

  useEffect(() => {
    setImageFile(imageFile => {
      if (imageFile) return imageFile;
      if (userContext?.profile?.photoUrl) return userContext.profile.photoUrl;
      return undefined;
    });
  }, [userContext?.profile?.photoUrl]);

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange
      initialValues={{
        firstName: userContext?.profile?.firstName ?? "",
        lastName: userContext?.profile?.lastName ?? "",
        organizationTitle: userContext?.coach?.title ?? "",
        company: userContext?.coach?.companyName ?? "",
        phoneNumber: userContext?.profile?.phone ?? "",
        contactPhone: userContext?.coach?.contactPhone ?? userContext?.profile?.phone ?? "",
        contactEmail: userContext?.coach?.contactEmail ?? userContext?.account?.email ?? "",
        directSupportToCoach: !!userContext?.coach?.directSupportToCoach,
      }}
      validationSchema={validationProfileSchema}
      onSubmit={async (
        {
          firstName,
          lastName,
          organizationTitle,
          company,
          phoneNumber,
          contactPhone,
          contactEmail,
          directSupportToCoach,
        },
        { setSubmitting }
      ) => {
        try {
          if (!userContext?.account) return;

          let urlImg = imageFile || "";
          if (img !== undefined) {
            urlImg = await saveImageToFirebase(config.firebase, userContext.account.userId, img, "main");
          }
          const response = await SocialCoachSessionService.updateCoach(
            userContext.coach?.id!,
            {
              profile: {
                phone: phoneNumber,
                firstName,
                lastName,
                fullName: `${firstName} ${lastName}`,
                photoUrl: urlImg,
              },
              coach: {
                id: userContext.coach?.id!,
                companyName: company,
                title: organizationTitle,
                contactEmail,
                contactPhone,
                directSupportToCoach,
              },
            },
            userContext.account.userId
          );
          if (response.profile.errors) {
            toast.error("An error occurred while updating the profile.");
          } else {
            toast.success("Profile updated successfully");
            refreshSession();
          }
        } catch {
          toast.error("An error occurred while updating the profile");
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, handleChange, isSubmitting, isValid, handleSubmit }) => (
        <form onSubmit={handleSubmit} className="flex flex-col items-center max-w-[1300px] min-w-3xl ml-[10px]">
          <div className="flex flex-col gap-5 w-3xl">
            <div className="flex flex-col items-center">
              <ImgCrop
                showGrid
                onModalOk={value => {
                  getImageAsBase64(value, (base64Image: string) => {
                    setImageFile(base64Image);
                    setImg(value as File);
                  });
                }}
              >
                {imageFile ? (
                  <Upload listType="picture">
                    <img
                      src={imageFile}
                      className="cursor-pointer w-55 h-55 min-w-55 min-h-55 shadow-xs rounded-full"
                    />
                  </Upload>
                ) : (
                  <Upload.Dragger listType="picture">
                    <div className="flex flex-col items-center justify-center w-55 h-55 rounded-xl text-black">
                      <UploadIcon className="w-9 h-9 text-muted-foreground mb-3" />
                      <p className="text-sm text-muted-foreground font-medium">Click or drag image</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">to upload a profile photo</p>
                    </div>
                  </Upload.Dragger>
                )}
              </ImgCrop>
              {imageFile && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-1 text-muted-foreground hover:text-red-500"
                  onClick={() => {
                    setImageFile(undefined);
                    setImg(undefined);
                  }}
                >
                  <Trash2 />
                  Remove
                </Button>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <Card>
                <Card.Header>
                  <Card.Title className="text-lg">Profile Information</Card.Title>
                </Card.Header>
                <Card.Content className="grid grid-cols-2 gap-4">
                  <Field label="First Name" name="firstName" error={errors.firstName}>
                    {props => (
                      <Input
                        {...props}
                        autoComplete="section-company given-name"
                        value={values.firstName}
                        placeholder="First Name"
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                    )}
                  </Field>
                  <Field label="Last Name" name="lastName" error={errors.lastName}>
                    {props => (
                      <Input
                        {...props}
                        autoComplete="section-company family-name"
                        value={values.lastName}
                        placeholder="Last Name"
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                    )}
                  </Field>
                  <Field label="Phone Number" name="phoneNumber" error={errors.phoneNumber}>
                    {props => (
                      <MaskedInput
                        {...props}
                        mask={phoneNumberMask}
                        value={values.phoneNumber}
                        onChange={handleChange}
                        render={(ref, props2) => (
                          <Input
                            ref={ref as Ref<HTMLInputElement>}
                            {...props2}
                            autoComplete="section-company tel"
                            placeholder="Phone Number"
                            disabled={isSubmitting}
                          />
                        )}
                      />
                    )}
                  </Field>
                  <Field label="Title" name="organizationTitle" error={errors.organizationTitle}>
                    {props => (
                      <Input
                        {...props}
                        autoComplete="section-company organization-title"
                        value={values.organizationTitle}
                        placeholder="Title"
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                    )}
                  </Field>
                </Card.Content>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title className="text-lg">Company Information</Card.Title>
                  <Card.Description>
                    This information will be displayed to Players within the SocialCoach app
                  </Card.Description>
                </Card.Header>
                <Card.Content className="grid grid-cols-2 gap-4">
                  <Field label="Name" name="company" error={errors.company}>
                    {props => (
                      <Input
                        {...props}
                        autoComplete="section-company organization"
                        value={values.company}
                        placeholder="Company"
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                    )}
                  </Field>
                  <Field label="Email" name="contactEmail" error={errors.contactEmail}>
                    {props => (
                      <Input
                        {...props}
                        autoComplete="work email"
                        value={values.contactEmail}
                        placeholder="Email"
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                    )}
                  </Field>
                  <Field label="Phone Number" name="contactPhone" error={errors.contactPhone}>
                    {props => (
                      <MaskedInput
                        {...props}
                        mask={phoneNumberMask}
                        value={values.contactPhone}
                        onChange={handleChange}
                        render={(ref, props2) => (
                          <Input
                            ref={ref as Ref<HTMLInputElement>}
                            {...props2}
                            autoComplete="work tel"
                            placeholder="Phone Number"
                            disabled={isSubmitting}
                          />
                        )}
                      />
                    )}
                  </Field>
                  <Field
                    label="Handle Player Support Requests"
                    name="directSupportToCoach"
                    error={errors.directSupportToCoach}
                  >
                    {props => (
                      <Switch
                        {...props}
                        checked={values.directSupportToCoach}
                        onCheckedChange={c => handleChange({ target: { name: "directSupportToCoach", value: c } })}
                        disabled={isSubmitting}
                      />
                    )}
                  </Field>
                </Card.Content>
              </Card>

              <div className="flex flex-col items-center gap-2">
                <Button size="lg" type="submit" disabled={isSubmitting || !isValid} className="w-50">
                  Save
                </Button>
                <UserChangePasswordForm
                  trigger={
                    <Button type="button" variant="ghost" className="w-50">
                      Change Password
                    </Button>
                  }
                />
              </div>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};
export default CoachProfile;
